import React, { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

interface Props {
  startDate: string;
  endDate: string;
  onApply: (start: string, end: string) => void;
  onCancel: () => void;
  onClear: () => void;
}

const presets = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'This month', range: [startOfMonth(new Date()), new Date()] },
  { label: 'Last month', range: [startOfMonth(subDays(new Date(), 30)), endOfMonth(subDays(new Date(), 30))] },
  { label: 'This year', range: [startOfYear(new Date()), new Date()] },
  { label: 'Last year', range: [startOfYear(subDays(new Date(), 365)), endOfYear(subDays(new Date(), 365))] },
];

const DateRangeFilter: React.FC<Props> = ({ startDate, endDate, onApply, onCancel, onClear }) => {
  const [start, setStart] = useState(startDate || '');
  const [end, setEnd] = useState(endDate || '');

  const applyPreset = (range: [Date, Date]) => {
    setStart(format(range[0], 'yyyy-MM-dd'));
    setEnd(format(range[1], 'yyyy-MM-dd'));
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md border w-[500px] grid grid-cols-3 p-4 gap-4">
      {/* Left: Presets */}
      <div className="col-span-1 border-r pr-4 space-y-2">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</h4>
        {presets.map((preset) => (
          <button
            key={preset.label}
            onClick={() =>
            applyPreset(
  (preset.range
    ? preset.range
    : [subDays(new Date(), preset.days!), new Date()]
  ) as [Date, Date]
)
            }
            className="text-left w-full text-sm text-gray-600 dark:text-gray-300 hover:text-emerald-600"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Right: Custom Inputs */}
      <div className="col-span-2 space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
            Start date
            {start && (
              <button onClick={() => setStart('')} className="text-xs text-emerald-600">
                Clear
              </button>
            )}
          </label>
          <input
            type="date"
            className="mt-1 block w-full border rounded px-2 py-1 text-sm"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
            End date
            {end && (
              <button onClick={() => setEnd('')} className="text-xs text-emerald-600">
                Clear
              </button>
            )}
          </label>
          <input
            type="date"
            className="mt-1 block w-full border rounded px-2 py-1 text-sm"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>

        {/* Bottom Buttons */}
        <div className="flex justify-between pt-4">
          <button
            className="px-4 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
            onClick={() => {
              setStart('');
              setEnd('');
              onClear();
            }}
          >
            Clear
          </button>
          <div className="flex gap-2">
            <button
              className="px-4 py-1 border rounded text-sm text-gray-600 hover:bg-gray-100"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
              onClick={() => onApply(start, end)}
              disabled={!start || !end}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;
